import { Form, Formik } from 'formik';
import { useContext } from 'react';
import useSWR from 'swr';
import { APISource } from '../../../../../../../../typings/Source.interface';
import APIMethodKeys from '../../../../../client/APIMethodKeys';
import Button from '../../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../../common/base/Card';
import Loading from '../../../../common/base/Loading';
import MultiSelectInput from '../../../../common/Form/Fields/MultiSelectInput';
import SelectResourceInput from '../../../../common/Form/Fields/SelectResourceInput';
import { Div } from '../../../../common/helpers/StyledUtils';
import { useToasts } from '../../../../common/Toast';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import { DashboardContext } from '../../DashboardContext';
import Text from '../../../../common/base/Text';
import SwitchInput from '../../../../common/Form/Fields/SwitchInput';
import Skeleton from '../../../../common/base/Skeleton';
import Tooltip from '../../../../common/base/Tooltip';
import Icon from '../../../../common/base/Icon';
const TeamNotifications: React.FC = () => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    team,
    mutateTeam
  } = useContext(DashboardContext);
  const {
    addToast
  } = useToasts();
  const {
    data: source
  } = useSWR(team?.webhook_source_id && APIMethodKeys.sources.get(team.webhook_source_id), () => HookdeckAPI.sources.get(team?.webhook_source_id as string));
  const handleSubmit = values => {
    return HookdeckAPI.teams.update({
      notification_methods: Object.entries(values.notification_methods).reduce((arr: any[], [method, checked]) => {
        if (checked) {
          arr.push(method);
        }
        return arr;
      }, []),
      webhook_topics: values.notification_methods.webhook && values.webhook_topics || null,
      webhook_source_id: values.notification_methods.webhook && values.webhook_source || null
    }).then(team_0 => {
      addToast('success', 'Project updated');
      mutateTeam(team_0);
      return true;
    }).catch(() => {
      addToast('error', 'Failed to update project');
      return false;
    });
  };
  const submitDisableWebhooks = values_0 => {
    return HookdeckAPI.teams.update({
      notification_methods: Object.entries(values_0.notification_methods).reduce((arr_0: any[], [method_0, checked_0]) => {
        if (checked_0 && method_0 !== 'webhook') {
          arr_0.push(method_0);
        }
        return arr_0;
      }, []),
      webhook_topics: null,
      webhook_source_id: null
    }).then(team_1 => {
      addToast('success', 'Webhook notifications disabled');
      mutateTeam(team_1);
      return true;
    }).catch(() => {
      addToast('error', 'Failed to update project');
      return false;
    });
  };
  return <>
      <Text bold size="l" as="h2" m={{
      b: 4
    }}>
        Project notifications
      </Text>
      <Loading require={team?.webhook_source_id ? [source] : []} wrapper={() => <>
            <Skeleton w={100} h={{
        px: 74
      }} loading m={{
        b: 4
      }} />
            <Skeleton w={100} h={{
        px: 74
      }} loading />
          </>}>
        {() => <Formik initialValues={{
        notification_methods: team!.notification_methods.reduce((obj, method_1) => ({
          ...obj,
          [method_1]: true
        }), {}),
        webhook_topics: team!.webhook_topics || [],
        webhook_source: source?.id || null
      }} validate={values_1 => {
        const errors: {
          webhook_source?: string;
        } = {};
        if (values_1.notification_methods.webhook && !values_1.webhook_source) {
          errors.webhook_source = 'Required';
        }
        return errors;
      }} onSubmit={handleSubmit}>
            {props => <Form>
                <StyledCard flex={{
            justify: 'flex-start',
            align: 'center',
            gap: 3
          }} m={{
            b: 4
          }} p={{
            x: 4,
            y: 3
          }}>
                  <SwitchInput name="notification_methods.email" onChange={() => props.submitForm()} />
                  <Div flex={{
              align: 'center',
              gap: 1
            }}>
                    <Text>Email notifications</Text>
                    <Tooltip tooltip="Every member of this project receives email notifications.">
                      <Icon muted icon="info" />
                    </Tooltip>
                  </Div>
                </StyledCard>
                <StyledCard m={{
            b: 4
          }}>
                  <StyledCardSection p={{
              x: 4,
              y: 3
            }} flex={{
              justify: 'flex-start',
              align: 'center',
              gap: 3
            }}>
                    <SwitchInput name="notification_methods.webhook" onChange={e => {
                if (e.target.checked === false) {
                  submitDisableWebhooks(props.values);
                }
              }} />
                    <Div flex={{
                align: 'center',
                gap: 1
              }}>
                      <Text>Webhook notifications</Text>
                      <Tooltip tooltip="A Hookdeck source URL receives webhook notifications.">
                        <Icon muted icon="info" />
                      </Tooltip>
                    </Div>
                  </StyledCardSection>

                  {props.values.notification_methods.webhook && <StyledCardSection p={4}>
                      <MultiSelectInput label="Webhook topics" name="webhook_topics" option_monospace required initial_options={['issue.opened', 'issue.updated', 'deprecated.attempt-failed', 'event.successful'].map(v => ({
                value: v,
                label: v
              }))} />
                      <SelectResourceInput<APISource> label="Webhook source" name="webhook_source" help="Select the source receiving webhooks. We recommend naming the source “Hookdeck”." listResources={HookdeckAPI.sources.list} listResourcesKey={APIMethodKeys.sources.list} />
                      <Button.Permission m={{
                t: 4
              }} primary disabled={!props.isValid || !props.touched || props.isSubmitting || props.values.webhook_source?.id === team?.webhook_source_id && props.values.webhook_topics.length === team?.webhook_topics?.length && props.values.webhook_topics.every(topic => team?.webhook_topics?.includes(topic))} submit icon={props.isSubmitting ? 'loading' : 'save'}>
                        Save
                      </Button.Permission>
                    </StyledCardSection>}
                </StyledCard>
              </Form>}
          </Formik>}
      </Loading>
    </>;
};
export default TeamNotifications;